import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useGlobalUIDispatch, useGlobalUIState } from '@olo-web/client-state';
import axios, { AxiosError } from 'axios';
import { useSendEvent } from '@olo-web/utils/common/hooks';
import { filterRecommendedItems } from '@olo-web/utils/common/functions/filterRecommendedItems';
import { useMenu } from '@olo-web/domain/menu/queries/useMenu';
import { mockGoogleAIReturn } from '@olo-web/utils/common/functions/mockGoogleAIReturn';
import { parseCookies } from 'nookies';
import {
  EAnalyticsEventNames,
  ERecommendationStatus,
  ERecommendationType,
  ECookies,
} from '@olo-web/types/enums';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';

const isNotProd = process.env.NEXT_PUBLIC_APP_ENVIRONMENT !== 'prod';

type OthersYouMayLikeProps = {
  merchantId: string;
  customerId: string;
  itemIDs: string[];
};

export const getOthersYouMayLike = async ({
  merchantId,
  customerId,
  itemIDs,
}: OthersYouMayLikeProps): Promise<IItemRecommendations> => {
  try {
    const options: { timeout?: number } = { timeout: 500 };
    const { data } = await axios.post(
      `/api/merchants/${merchantId}/customers/${customerId}/others-you-may-like`,
      itemIDs,
      options
    );
    if (isNotProd) {
      return mockGoogleAIReturn;
    }
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useOthersYouMayLike = (
  options?: UseMutationOptions<any, AxiosError, OthersYouMayLikeProps>
) => {
  const globalUIDispatch = useGlobalUIDispatch();
  const { googleAttributionToken } = useGlobalUIState();
  const { data: menu } = useMenu();
  const { sendEvent } = useSendEvent();
  const queryClient = useQueryClient();
  const cookies = parseCookies();
  const { data: order } = useOrder();
  const itemIDs = order?.items?.map((item) => item.menuItemLocationId) || [];

  return useMutation(getOthersYouMayLike, {
    ...options,
    onSuccess: (data) => {
      const filteredItems = filterRecommendedItems(data.results, menu);
      queryClient.setQueryData(
        ['item-others-you-may-like', cookies[ECookies.userId], itemIDs.join(',')],
        filteredItems
      );
      if (!googleAttributionToken) {
        globalUIDispatch({
          type: 'SET_GOOGLE_ATTRIBUTION_TOKEN',
          payload: data.attributionToken,
        });
      }
      const recommendedItems = filteredItems.map((item, index) => {
        const recItem = {
          itemName: item.name,
          itemPrice: item.price,
          displayPosition: index + 1, // Starting displayPosition from 1
        };
        return JSON.stringify(recItem);
      });

      const googleAnalytics = {
        recommendedItems: JSON.stringify(recommendedItems),
        recommendationStatus: filteredItems.length
          ? ERecommendationStatus.DISPLAYED
          : ERecommendationStatus.NO_RESULTS,
        recommendationType: ERecommendationType.CHECKOUT,
      };

      sendEvent(EAnalyticsEventNames.GOOGLE_SUGGESTIONS_LOADED, { googleAnalytics });
    },
    onError: (error) => {
      const googleAnalytics = {
        recommendationStatus:
          error.code === 'ECONNABORTED'
            ? ERecommendationStatus.TIMEOUT
            : ERecommendationStatus.ERROR,
        recommendationType: ERecommendationType.CHECKOUT,
      };

      sendEvent(EAnalyticsEventNames.GOOGLE_SUGGESTIONS_LOADED, { googleAnalytics });
    },
  });
};
